import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataTable, PageHeader, SectionCard } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

type OpsTenant = {
  store_id: number;
  name: string;
  account_status: string | null;
  locked: boolean;
  ops_status: string;
  plan_code: string | null;
  plan_price_monthly: number;
  stripe_subscription_status: string | null;
  last_payment_status: string | null;
  last_payment_error: string | null;
  last_invoice_status: string | null;
  trial_or_period_end: string | null;
  upcoming_invoice_at: string | null;
  next_retry_at: string | null;
  payment_method_expired: boolean;
  last_checkout_status: string | null;
  last_stripe_event_type: string | null;
  last_stripe_event_at: string | null;
  module_subscription_count: number;
  sms_credits_balance: number | null;
  remediation: string[];
  id?: number;
};

type WebhookRow = {
  event_id: string;
  event_type: string;
  critical: boolean;
  handled: boolean;
  store_ids: number[];
  processed_at: string | null;
  id?: string;
};

type OpsPayload = {
  kpis?: {
    total_tenants: number;
    by_account_status: Record<string, number>;
    payment_failed: number;
    payment_retrying: number;
    payment_method_expired: number;
    billing_blocked: number;
    sms_low_balance: number;
    critical_webhooks_recent: number;
  };
  tenants?: OpsTenant[];
  webhooks?: WebhookRow[];
  critical_events?: WebhookRow[];
  meta?: { generated_at?: string };
  error?: string;
  message?: string;
};

function remediationLabel(code: string): string {
  const map: Record<string, string> = {
    update_payment: 'Aggiorna pagamento',
    start_checkout: 'Avvia checkout',
    update_payment_method: 'Aggiorna metodo',
    extend_trial_or_checkout: 'Estendi trial / checkout',
  };
  return map[code] || code;
}

export function MasterBillingRegistryPage() {
  const [payload, setPayload] = useState<OpsPayload | null>(null);
  const [summary, setSummary] = useState<{
    kpis?: {
      total_accounts: number;
      active_or_trial: number;
      delinquent_accounts_count: number;
      critical_anomalies: number;
    };
    tenants?: Array<{ store_id: number; module_mrr: number; active_module_count: number; plan_code: string | null }>;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function load() {
    const [opsRes, sumRes] = await Promise.all([
      apiFetch('/api/v1/master/billing/ops'),
      apiFetch('/api/v1/master/billing/summary'),
    ]);
    const body = (await opsRes.json().catch(() => ({}))) as OpsPayload;
    if (!opsRes.ok) throw new Error(body.message || body.error || `HTTP ${opsRes.status}`);
    setPayload(body);
    if (sumRes.ok) {
      setSummary((await sumRes.json().catch(() => ({}))) as typeof summary);
    }
  }

  useEffect(() => {
    void load().catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, []);

  const kpis = payload?.kpis;
  const tenants = useMemo(() => {
    const list = Array.isArray(payload?.tenants) ? payload!.tenants! : [];
    return list
      .filter((t) => {
        if (statusFilter !== 'ALL' && String(t.account_status || '') !== statusFilter) return false;
        if (problemsOnly) {
          const problem =
            t.locked ||
            t.payment_method_expired ||
            ['failed', 'retrying', 'unpaid'].includes(String(t.last_payment_status || '')) ||
            (t.remediation && t.remediation.length > 0);
          if (!problem) return false;
        }
        return true;
      })
      .map((t) => ({ ...t, id: t.store_id }));
  }, [payload, statusFilter, problemsOnly]);

  const webhooks = useMemo(() => {
    const list = Array.isArray(payload?.webhooks) ? payload!.webhooks! : [];
    return list.map((w) => ({ ...w, id: w.event_id }));
  }, [payload]);

  async function remediate(storeId: number, action: string) {
    setBusyId(storeId);
    setToast(null);
    setError(null);
    try {
      if (action === 'update_payment' || action === 'update_payment_method') {
        const res = await apiFetch('/api/v1/tenant/billing/portal', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            storeId,
            returnUrl: `${window.location.origin}/master/billing`,
          }),
        });
        const body = (await res.json().catch(() => ({}))) as {
          url?: string;
          message?: string;
          error?: string;
        };
        if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
        if (!body.url) throw new Error('Portal URL mancante');
        window.open(body.url, '_blank', 'noopener,noreferrer');
        setToast(`Portal Stripe aperto per store ${storeId}`);
      } else if (action === 'start_checkout' || action === 'extend_trial_or_checkout') {
        const res = await apiFetch('/api/v1/master/billing/checkout-session', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ storeId, planCode: 'free' }),
        });
        const body = (await res.json().catch(() => ({}))) as {
          url?: string;
          message?: string;
          error?: string;
        };
        if (!res.ok) throw new Error(body.message || body.error || `HTTP ${res.status}`);
        if (!body.url) throw new Error('Checkout URL mancante');
        window.open(body.url, '_blank', 'noopener,noreferrer');
        setToast(`Checkout aperto per store ${storeId}`);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }

  const by = kpis?.by_account_status || {};

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Billing ops"
        subtitle={
          <>
            <Link to="/master">← Master</Link> · stato Stripe reale ·{' '}
            <Link to="/master/tenants">azioni Tenant</Link>
          </>
        }
      />
      <p className="sk-mc-hint" style={{ marginTop: '0.65rem' }}>
        Metriche operative (no MRR inventato, no fatture fake). Azioni remediation → Portal /
        Checkout.
        {payload?.meta?.generated_at
          ? ` · ${new Date(payload.meta.generated_at).toLocaleString('it-IT')}`
          : ''}
      </p>

      {toast ? <p className="sk-ok">{toast}</p> : null}
      {error ? <p className="sk-error">{error}</p> : null}

      {kpis ? (
        <div className="sk-saas-kpi" style={{ marginTop: '0.25rem' }}>
          <div className="sk-saas-kpi__tile">
            <p className="sk-saas-kpi__label">Tenant</p>
            <p className="sk-saas-kpi__value">{kpis.total_tenants}</p>
          </div>
          <div className="sk-saas-kpi__tile">
            <p className="sk-saas-kpi__label">Active / Trial</p>
            <p className="sk-saas-kpi__value">
              {(by.ACTIVE || 0) + (by.TRIAL || 0)}
            </p>
          </div>
          <div className="sk-saas-kpi__tile">
            <p className="sk-saas-kpi__label">Past due / sospesi</p>
            <p className="sk-saas-kpi__value">
              {(by.PAST_DUE || 0) + (by.SUSPENDED || 0)}
            </p>
          </div>
          <div className="sk-saas-kpi__tile">
            <p className="sk-saas-kpi__label">Pagamenti fail/retry</p>
            <p className="sk-saas-kpi__value">
              {kpis.payment_failed}/{kpis.payment_retrying}
            </p>
          </div>
          <div className="sk-saas-kpi__tile">
            <p className="sk-saas-kpi__label">PM scaduti</p>
            <p className="sk-saas-kpi__value">{kpis.payment_method_expired}</p>
          </div>
          <div className="sk-saas-kpi__tile">
            <p className="sk-saas-kpi__label">Bloccati / SMS low</p>
            <p className="sk-saas-kpi__value">
              {kpis.billing_blocked}/{kpis.sms_low_balance}
            </p>
          </div>
          <div className="sk-saas-kpi__tile">
            <p className="sk-saas-kpi__label">Webhook critical</p>
            <p className="sk-saas-kpi__value">{kpis.critical_webhooks_recent}</p>
          </div>
          {summary?.kpis ? (
            <>
              <div className="sk-saas-kpi__tile">
                <p className="sk-saas-kpi__label">Summary active/trial</p>
                <p className="sk-saas-kpi__value">{summary.kpis.active_or_trial}</p>
              </div>
              <div className="sk-saas-kpi__tile">
                <p className="sk-saas-kpi__label">Delinquent</p>
                <p className="sk-saas-kpi__value">{summary.kpis.delinquent_accounts_count}</p>
              </div>
              <div className="sk-saas-kpi__tile">
                <p className="sk-saas-kpi__label">Anomalie critical</p>
                <p className="sk-saas-kpi__value">{summary.kpis.critical_anomalies}</p>
              </div>
              <div className="sk-saas-kpi__tile">
                <p className="sk-saas-kpi__label">Σ module MRR €</p>
                <p className="sk-saas-kpi__value">
                  {(summary.tenants || [])
                    .reduce((sum, t) => sum + (Number(t.module_mrr) || 0), 0)
                    .toFixed(0)}
                </p>
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      <SectionCard
        title={`${tenants.length} tenant`}
        actions={
          <div style={{ display: 'flex', gap: '0.65rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <label className="sk-field" style={{ margin: 0 }}>
              <span className="sk-mc-hint">Stato</span>
              <select
                className="sk-input"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">Tutti</option>
                <option value="TRIAL">TRIAL</option>
                <option value="ACTIVE">ACTIVE</option>
                <option value="PAST_DUE">PAST_DUE</option>
                <option value="SUSPENDED">SUSPENDED</option>
              </select>
            </label>
            <label className="sk-field" style={{ margin: 0, flexDirection: 'row', gap: '0.4rem' }}>
              <input
                type="checkbox"
                checked={problemsOnly}
                onChange={(e) => setProblemsOnly(e.target.checked)}
              />
              <span>Solo problemi</span>
            </label>
          </div>
        }
      >
        <DataTable
          emptyMessage="Nessun tenant nel filtro"
          rows={tenants}
          columns={[
            { key: 'store_id', label: 'Store' },
            { key: 'name', label: 'Nome' },
            {
              key: 'account_status',
              label: 'Account',
              render: (v, row) =>
                `${String(v || '—')}${row.locked ? ' · LOCK' : ''} · ${row.ops_status}`,
            },
            {
              key: 'stripe_subscription_status',
              label: 'Sub Stripe',
              render: (v) => String(v || '—'),
            },
            {
              key: 'last_payment_status',
              label: 'Pagamento',
              render: (v, row) =>
                `${String(v || '—')}${row.last_invoice_status ? ` / inv ${row.last_invoice_status}` : ''}`,
            },
            {
              key: 'plan_code',
              label: 'Piano',
              render: (v, row) =>
                `${String(v || 'free')} · €${Number(row.plan_price_monthly || 0).toFixed(0)}/m`,
            },
            {
              key: 'trial_or_period_end',
              label: 'Scadenza / rinnovo',
              render: (_v, row) => {
                const end = row.trial_or_period_end
                  ? new Date(row.trial_or_period_end).toLocaleDateString('it-IT')
                  : '—';
                const next = row.upcoming_invoice_at
                  ? new Date(row.upcoming_invoice_at).toLocaleDateString('it-IT')
                  : null;
                return next ? `${end} · next ${next}` : end;
              },
            },
            {
              key: 'sms_credits_balance',
              label: 'SMS',
              render: (v, row) =>
                `${v == null ? '—' : String(v)}${row.module_subscription_count ? ` · sub ${row.module_subscription_count}` : ''}`,
            },
            {
              key: 'last_stripe_event_type',
              label: 'Ultimo evento',
              render: (v, row) =>
                v
                  ? `${String(v)}${row.last_stripe_event_at ? ` · ${new Date(row.last_stripe_event_at).toLocaleString('it-IT')}` : ''}`
                  : '—',
            },
            {
              key: 'remediation',
              label: 'Remediation',
              render: (_v, row) =>
                row.remediation?.length ? (
                  <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                    {row.remediation.map((code) => (
                      <button
                        key={code}
                        type="button"
                        className="sk-btn sk-btn--quiet"
                        disabled={busyId === row.store_id}
                        onClick={() => void remediate(row.store_id, code)}
                      >
                        {remediationLabel(code)}
                      </button>
                    ))}
                    <Link className="sk-btn sk-btn--quiet" to={`/master/tenants?storeId=${row.store_id}`}>
                      Tenant
                    </Link>
                  </span>
                ) : (
                  <Link to={`/master/tenants?storeId=${row.store_id}`}>Gestisci</Link>
                ),
            },
          ]}
        />
      </SectionCard>

      <SectionCard title={`Webhook Stripe recenti (${webhooks.length})`}>
        <p className="sk-mc-hint">Solo eventi handled (filtered). Critical in evidenza.</p>
        <DataTable
          emptyMessage="Nessun webhook ancora"
          rows={webhooks}
          columns={[
            {
              key: 'critical',
              label: '!',
              render: (v) => (v ? 'CRIT' : ''),
            },
            { key: 'event_type', label: 'Tipo' },
            {
              key: 'store_ids',
              label: 'Stores',
              render: (v) => (Array.isArray(v) && v.length ? v.join(',') : '—'),
            },
            {
              key: 'processed_at',
              label: 'Quando',
              render: (v) => (v ? new Date(String(v)).toLocaleString('it-IT') : '—'),
            },
            { key: 'event_id', label: 'Event' },
          ]}
        />
      </SectionCard>
    </section>
  );
}
